import React from 'react';
import { ChevronDown } from 'lucide-react';
import { IntegrationLogo, GIGA_MARK } from '../IntegrationLogo';
import { EXPORT_BODY, EXPORT_META } from './exportUi';

/**
 * GIGA ANSWERING INSIDE CHATGPT — AN EXPORT-ONLY ASSET.
 *
 * WHY NOT `ChatGPTPanel`. That component rebuilds the whole ChatGPT
 * application: sidebar, recents list, account row, model dropdown, composer,
 * and a four-turn transcript. On the page it earns that, because the point
 * there is "this is really ChatGPT". As a downloadable asset it fails, and for
 * a specific reason: at the size an asset is actually viewed — a card on a
 * slide, an overlay on a phone video — the sidebar and composer eat half the
 * frame, the transcript runs past what anyone will read, and nothing in it is
 * legible enough to make the one point that matters.
 *
 * That point is narrow: ChatGPT asked Giga, Giga went to a real system, and
 * the answer came back with the record it came from. So this frame is exactly
 * that, at a size you can read from across a room:
 *
 *   · Enough ChatGPT chrome to be recognisably ChatGPT — the mark and the
 *     model name — and no more. No sidebar, no composer, no account row.
 *   · ONE question, in the user's own bubble.
 *   · The Giga call, made visible as a step rather than hidden: the mark, the
 *     system it reached, and what it took. This is the whole asset; it is the
 *     only element that gets the accent.
 *   · The answer, with the source named underneath.
 *
 * Static by design, like every frame on the export page.
 */

/** The three records behind the answer. Named, because "from your CRM" is a
 * claim and "Northwind · Q3 renewal" is a fact. */
const SOURCES: [string, string][] = [['salesforce', 'Salesforce'], ['gmail', 'Gmail']];
export function GigaInChatGPT({
  /** Phone-width artboards drop the model row's right side and tighten the
   * gutters — the transcript itself is already a single column. */
  portrait = false
}: {
  portrait?: boolean;
} = {}) {
  return <div className="w-full overflow-hidden rounded-[20px] border border-[#E5E5E5] bg-white shadow-[0_1px_2px_rgba(43,33,26,0.05),0_18px_40px_-18px_rgba(43,33,26,0.22)]" data-id="element-3280" data-mp="u01ugng2zsnl">
      {/* ChatGPT's own chrome, reduced to the two things that identify it. */}
      <header className="flex items-center justify-between border-b border-[#F0F0F0] px-5 py-3.5" data-id="element-3281" data-mp="13spq3evbrqkq">
        <span className="flex items-center gap-2.5" data-id="element-3282" data-mp="yxtktd12v6po">
          <IntegrationLogo slug="openai" name="ChatGPT" className="size-[19px]" rounded="rounded-full" data-id="element-3283" data-mp="1dnrda561iiko" />
          <span className="text-[15px] font-medium text-[#0D0D0D]" data-id="element-3284" data-mp="18oo9sp5pkket">
            ChatGPT <span className="text-[#8F8F8F]" data-id="element-3285" data-mp="1mq0lgodu5zuw">5</span>
          </span>
          <ChevronDown size={14} className="text-[#8F8F8F]" data-id="element-3286" data-mp="1mn4nx15whd6z" />
        </span>
        {!portrait && <span className="rounded-full border border-[#E5E5E5] px-2.5 py-1 text-[11.5px] font-semibold text-[#5D5D5D]" data-id="element-3287" data-mp="1hob7p0z8b834">
            Giga connected
          </span>}
      </header>

      <div className={`flex flex-col gap-5 ${portrait ? 'px-4 py-5' : 'px-6 py-6'}`} data-id="element-3288" data-mp="3jjbwrykr4q18">
        {/* The question, in ChatGPT's own bubble treatment. */}
        <p className="max-w-[85%] self-end rounded-[20px] bg-[#F4F4F4] px-4 py-2.5 text-[15px] leading-[1.6] text-[#0D0D0D]" data-id="element-3289" data-mp="23yjmjwd9xf7j">
          Which renewals are at risk this quarter, and why?
        </p>

        {/* THE ASSET. The call out to Giga, shown as a step that happened —
         this is the only element carrying the accent, because it is the only
         thing this frame exists to say. */}
        <div className="rounded-[16px] border border-[#EFE7DA] bg-[#FAF8F4] px-4 py-3.5" data-id="element-3290" data-mp="1l81wjzjtsxpv">
          <div className="flex flex-wrap items-center gap-2" data-id="element-3291" data-mp="178twl4fu9lkm">
            <img src={GIGA_MARK} alt="" aria-hidden="true" className="size-[17px] flex-none object-contain" data-id="element-3292" data-mp="17adc2sto7slc" />
            <span className="text-[13px] font-bold text-[#2B211A]" data-id="element-3293" data-mp="1bc8g452zkk2t">Giga</span>
            <span className="text-[#D8CFC2]" data-id="element-3294" data-mp="31n375dvrz7ub">·</span>
            <span className={EXPORT_META} data-id="element-3295" data-mp="35nwzqkk5xatj">Reading your systems</span>
            <span className="ml-auto flex items-center gap-1.5" data-id="element-3296" data-mp="35m2iphc0i2uq">
              {SOURCES.map(([slug, name]) => <span key={slug} className="flex size-[26px] items-center justify-center rounded-full border border-[#EFE7DA] bg-white" data-id="element-3297" data-mp="2gvg3s1baqs2w">
                  <IntegrationLogo slug={slug} name={name} className="size-[14px]" rounded="rounded" data-id="element-3298" data-mp="26ytkhgymc92z" />
                </span>)}
            </span>
          </div>
          <p className={`${EXPORT_BODY} mt-2.5`} data-id="element-3299" data-mp="222cltstsmcbn">
            18 open renewals · 3 flagged · 12 email threads read
          </p>
        </div>

        {/* The answer, unbubbled — the way an assistant reply actually reads in
         ChatGPT — with the record named so the claim is checkable. */}
        <div data-id="element-3300" data-mp="1o6r2o2a8qmr5">
          <p className="text-[15px] leading-[1.7] text-[#0D0D0D]" data-id="element-3301" data-mp="1o6ircetrnpi2">
            Three are at risk. <strong className="font-semibold" data-id="element-3302" data-mp="1j7nxmhu9ggj6">Northwind Systems</strong>{' '}
            ($84k, closes 22 Sep) has had no executive contact in 46 days.{' '}
            <strong className="font-semibold" data-id="element-3303" data-mp="1aa57x05o9e0y">Meridian Health</strong> ($61k) lost its
            champion in July with no replacement named.{' '}
            <strong className="font-semibold" data-id="element-3304" data-mp="3lys59y456bsf">Fairline Group</strong> ($37k) is down 38% in
            usage month over month.
          </p>
          <p className={`${EXPORT_META} mt-3 flex flex-wrap items-center gap-x-2 gap-y-1`} data-id="element-3305" data-mp="3uwucwwids2oo">
            <span className="font-semibold text-[#2B211A]" data-id="element-3306" data-mp="3rcgnvsrrhcu">Sources</span>
            <span className="text-[#D8CFC2]" data-id="element-3307" data-mp="328gfahv7f8dn">·</span>
            Salesforce opportunities, 3 records
            <span className="text-[#D8CFC2]" data-id="element-3308" data-mp="377j9f79nbu6u">·</span>
            Gmail threads, 12 read
          </p>
        </div>
      </div>
    </div>;
}